import React, { useState } from 'react'
import { LayerProvider, SupportedLocale } from '@layerfi/components'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'
import useSWR from 'swr'

import { getLoginRedirectPath } from './authRedirects'
import { AutoTranslate } from './AutoTranslate'
import { mapDemo } from './components/admin/utils'
import {
  isBasisToggleEnabled,
  ReportingBasisProvider,
} from './components/ReportingBasisToggle'
import { Sidebar } from './components/Sidebar'
import { useAuth } from './contexts/AuthContext'
import { useDemoViewerSettings } from './contexts/DemoViewerSettingsContext'
import { useSidebarContext } from './contexts/SidebarContext'
import {
  getLayerEnvironmentConfig,
  type LayerEnvironment,
  toEnvironmentConfigOverride,
} from './layerEnvironments'
import { FourOhFour } from './pages/404'
import { CreateBusiness } from './pages/CreateBusiness'
import { ImportDummyData } from './pages/dataImportFlow/ImportDummyData'
import { Loading } from './pages/Loading'
import { useLayerAccessToken } from './useLayerAccessToken'

export const SOLOPRENEUR_DEMOS = [
  'TailorBrands',
  'JobberSolopreneur',
] as const

export interface Demo {
  id: string
  clientName: string
  brandColorHex: string
  brandColorLightHex: string
  brandColorDarkHex: string
  brandColorTextHex: string
  brandLogoUrl: string
  businessId?: string
  datePickerMode?: string
  hasImportedRevenueData?: boolean
  bookkeepingOn: boolean
  showDescriptions: boolean
  showReceiptUploads: boolean
  showInvoicesTab: boolean
  showCustomerVendor: boolean
  defaultToMobile: boolean
  useSolopreneurLayout: boolean
  enableTimeTracking: boolean
  translateToSpanish: boolean
  hasCredentialOverride?: boolean
  layerEnvironmentOverride?: LayerEnvironment
  archivedAt?: string
}

export const LAYER_API_URL = getLayerEnvironmentConfig().apiUrl

export const DEFAULT_ACCOUNT_NAME = 'Demo Credit Card'
export const DEFAULT_DEPOSITORY_ACCOUNT_NAME = 'Demo Checking'

// Legacy account names used by demos created before the rename. Kept so that
// lookups find and reuse existing accounts instead of creating duplicates.
export const LEGACY_DEFAULT_ACCOUNT_NAME = 'Layer Demo Credit Card'
export const LEGACY_DEFAULT_DEPOSITORY_ACCOUNT_NAME = 'Layer Demo Checking'

export const Root = () => {
  const { demoName } = useParams()
  const { clientName, isLoggedIn, isAdmin, authFetch } = useAuth()
  const location = useLocation()
  const isAuthorizedForDemo = !!demoName && isLoggedIn && (clientName === demoName || isAdmin)

  const { data: tokenData, error: tokenError, mutate: refreshToken } = useLayerAccessToken(
    isAuthorizedForDemo ? `/api/demos/${demoName}/access-token` : null,
  )
  const bearerToken = tokenData?.access_token ?? null

  const { data: demo, isLoading: isDemoLoading, mutate: mutateDemo } = useSWR(
    isAuthorizedForDemo ? `/api/demos/${demoName}` : null,
    async (url: string) => {
      const response = await authFetch(url)
      if (!response.ok) throw new Error(`Fetch demo data failed: ${response.status}`)
      return mapDemo(await response.json())
    },
    { revalidateOnFocus: false, revalidateOnReconnect: false, shouldRetryOnError: false },
  )

  const handleBusinessCreated = (createdBusinessId: string) => {
    refreshToken(undefined)
    void mutateDemo(
      currentDemo => currentDemo && { ...currentDemo, businessId: createdBusinessId },
      { revalidate: false },
    )
  }

  if (!isLoggedIn) {
    return <Navigate to={getLoginRedirectPath(location.pathname, location.search, location.hash)} replace />
  }

  if (!isAuthorizedForDemo) {
    return <FourOhFour />
  }

  if (isDemoLoading) {
    return <Loading />
  }

  if (!demo || (tokenError && !tokenData)) {
    return <FourOhFour />
  }

  if (!demo.businessId) {
    if (!isAdmin) {
      return <FourOhFour />
    }
    if (!bearerToken) {
      return <Loading />
    }
    return (
      <CreateBusiness
        demoName={demo.clientName}
        bearerToken={bearerToken}
        layerApiUrl={getLayerEnvironmentConfig(demo.layerEnvironmentOverride).apiUrl}
        onBusinessCreated={handleBusinessCreated}
      />
    )
  }

  if (!bearerToken) {
    return <Loading />
  }

  return (
    <DemoView
      key={demoName}
      demo={demo}
      businessId={demo.businessId}
      bearerToken={bearerToken}
    />
  )
}

interface DemoViewProps {
  demo: Demo
  businessId: string
  bearerToken: string
}

const DemoView = ({ demo, businessId, bearerToken }: DemoViewProps) => {
  const { isSidebarOpen } = useSidebarContext()
  const { settings: viewerSettings } = useDemoViewerSettings()
  const [importingTransactions, setImportingTransactions] = useState(false)
  const [mobileViewOverride, setIsMobileView] = useState<boolean>()
  const [showTooltips, setShowTooltips] = useState(false)
  const [bookkeepingModeOverride, setBookkeepingMode] = useState<boolean>()
  const [locale, setLocale] = useState<SupportedLocale>(SupportedLocale.enUS)
  const [layerProviderKey, setLayerProviderKey] = useState(0)
  const isMobileView = mobileViewOverride ?? demo.defaultToMobile
  const isBookkeepingMode = bookkeepingModeOverride ?? demo.bookkeepingOn
  const layerEnvironment = getLayerEnvironmentConfig(demo.layerEnvironmentOverride)

  if (importingTransactions) {
    return (
      <ImportDummyData
        demoName={demo.clientName}
        businessId={businessId}
        businessAccessToken={bearerToken}
        layerApiUrl={layerEnvironment.apiUrl}
        setBookkeepingMode={setBookkeepingMode}
        setImportingTransactions={setImportingTransactions}
      />
    )
  }
  const isSidebarHidden = process.env.REACT_APP_HIDE_SIDEBAR === 'true'
  const sidebarStatus = isSidebarOpen
    ? 'open'
    : isSidebarHidden
      ? 'hidden'
      : 'closed'

  return (
    <ReportingBasisProvider
      enabled={isBasisToggleEnabled(demo.clientName)}
      businessId={businessId}
      bearerToken={bearerToken}
      layerApiUrl={layerEnvironment.apiUrl}
      onMutate={() => setLayerProviderKey(k => k + 1)}
    >
    <LayerProvider
      key={layerProviderKey}
      businessId={businessId}
      environmentConfigOverride={toEnvironmentConfigOverride(layerEnvironment)}
      businessAccessToken={bearerToken}
      theme={{
        colors: {
          light: { hex: demo.brandColorLightHex },
          dark: { hex: demo.brandColorDarkHex },
          text: { hex: demo.brandColorTextHex },
        },
      }}
      usePlaidSandbox={layerEnvironment.usePlaidSandbox}
      locale={locale}
    >
      <AutoTranslate enabled={demo.translateToSpanish}>
        <div
          className={`structure sidebar--${sidebarStatus}${!viewerSettings.sidebarAutoCollapse ? ' sidebar--no-auto-collapse' : ''}`}
          style={{ '--sidebar-width': `${viewerSettings.sidebarWidth}px` } as React.CSSProperties}
        >
          <Sidebar
            brandLogoUrl={demo.brandLogoUrl}
            demoName={demo.clientName}
            setImportingTransactions={setImportingTransactions}
            isMobileView={isMobileView}
            setIsMobileView={setIsMobileView}
            isBookkeepingMode={isBookkeepingMode}
            setBookkeepingMode={setBookkeepingMode}
            showInvoicesTab={demo.showInvoicesTab}
            showTooltips={showTooltips}
            setShowTooltips={setShowTooltips}
            isSidebarHidden={isSidebarHidden}
            locale={locale}
            setLocale={setLocale}
            forceEnableTimeTracking={demo.enableTimeTracking}
            useSolopreneurLayout={demo.useSolopreneurLayout}
            enableTimeTracking={demo.enableTimeTracking}
          />
          <section className='main-content'>
            <Outlet
              context={{
                businessAccessToken: bearerToken,
                businessId,
                datePickerMode: demo.datePickerMode,
                isMobileView,
                demoName: demo.clientName,
                isBookkeepingMode,
                showDescriptions: demo.showDescriptions,
                showReceiptUploads: demo.showReceiptUploads,
                showInvoicesTab: demo.showInvoicesTab,
                showCustomerVendor: demo.showCustomerVendor,
                showTooltips,
                forceEnableTimeTracking: demo.enableTimeTracking,
                useSolopreneurLayout: demo.useSolopreneurLayout,
                defaultToMobile: demo.defaultToMobile,
                isSidebarHidden,
                brandLogoUrl: demo.brandLogoUrl,
              }}
            />
          </section>
        </div>
      </AutoTranslate>
    </LayerProvider>
    </ReportingBasisProvider>
  )
}
